var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { IconButton, Slider } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import FileSaver from 'file-saver';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './audioPlayer.module.scss';
const getFormattedDuration = (duration) => {
    const hours = Math.floor(duration / 60 / 60);
    const minutes = Math.floor(duration / 60) % 60;
    const seconds = Math.floor(duration) % 60;
    const hourDuration = `${hours}`.padStart(2, '0');
    const minuteDuration = `${minutes}`.padStart(2, '0');
    const secondDuration = `${seconds}`.padStart(2, '0');
    return `${hours ? `${hourDuration}:` : ''}${minuteDuration}:${secondDuration}`;
};
export function AudioPlayer({ className, onDelete, src, filename, disabled, isDownloadable, }) {
    const playerRef = useRef(null);
    const [{ elapsed, duration }, setTime] = useState({
        elapsed: 0,
        duration: 0,
    });
    const [isPlaying, setIsPlaying] = useState(false);
    const onPlayToggle = useCallback(() => {
        const { current: player } = playerRef;
        if (!player || player.error) {
            return;
        }
        if (isPlaying) {
            player.pause();
            setIsPlaying(false);
            return;
        }
        player.play();
        setIsPlaying(true);
    }, [playerRef, isPlaying, setIsPlaying]);
    const onDurationChange = () => {
        const { current: player } = playerRef;
        if (!player) {
            return;
        }
        const isLengthLoaded = player.duration !== Infinity;
        setTime({
            elapsed: isLengthLoaded ? player.currentTime : 0,
            duration: isLengthLoaded ? player.duration : 0,
        });
    };
    const onAudioEnd = () => setIsPlaying(false);
    const onSliderMouseDown = () => {
        /*
         * When we start dragging we want to pause the audio playback,
         * otherwise we will be getting some insane alien rave sounds.
         */
        const { current: player } = playerRef;
        if (!player) {
            return;
        }
        const handleMouseUp = () => {
            window.removeEventListener('mouseup', handleMouseUp);
            setIsPlaying(true);
            player.play();
        };
        setIsPlaying(false);
        player.pause();
        window.addEventListener('mouseup', handleMouseUp);
    };
    const onSeek = (value) => {
        const { current: player } = playerRef;
        const elapsed = Array.isArray(value) ? value[1] : value;
        if (!player) {
            return;
        }
        player.currentTime = elapsed;
    };
    const onSave = () => __awaiter(this, void 0, void 0, function* () {
        if (!src) {
            return;
        }
        try {
            const response = yield fetch(src, {
                method: 'GET',
                mode: 'cors',
                cache: 'no-cache',
            });
            const blob = yield response.blob();
            const filenameFromURL = decodeURIComponent(src.split('/').pop() || `recording-${Date.now()}`);
            FileSaver.saveAs(blob, filename || filenameFromURL);
        }
        catch (err) {
            console.error('Failed to download audio file:', err);
        }
    });
    useEffect(() => {
        setIsPlaying(false);
        setTime({ elapsed: 0, duration: 0 });
    }, [src]);
    const isDisabled = disabled || !src;
    const rootClass = classnames(styles.root, className, {
        [styles.rootIsDisabled]: disabled,
    });
    return (_jsxs("div", Object.assign({ className: rootClass }, { children: [_jsx("audio", { ref: playerRef, src: src, onDurationChange: onDurationChange, onTimeUpdate: onDurationChange, onEnded: onAudioEnd }), _jsxs("div", Object.assign({ className: styles.controls }, { children: [_jsx(IconButton, { icon: isPlaying ? 'pause' : 'play', variant: "secondary", className: styles.button, onClick: onPlayToggle, disabled: isDisabled }), _jsx(IconButton, { icon: "download", variant: "secondary", className: styles.button, disabled: !isDownloadable || isDisabled, onClick: onSave }), _jsxs("div", Object.assign({ className: classnames(styles.progress, {
                            [styles.progressIsDisabled]: isDisabled,
                        }) }, { children: [_jsx("span", Object.assign({ className: styles.duration }, { children: getFormattedDuration(elapsed) })), _jsx("div", Object.assign({ className: styles.slider, onMouseDown: onSliderMouseDown }, { children: _jsx(Slider, { hideAnnotations: true, min: 0, max: duration, value: elapsed, onChange: onSeek }) })), _jsx("span", Object.assign({ className: styles.duration }, { children: getFormattedDuration(duration) }))] })), onDelete && (_jsx(IconButton, { icon: "trash", variant: "secondary", disabled: isDisabled, onClick: onDelete }))] }))] })));
}
